/**
 * hooks/useDocumentOverride.ts
 *
 * Provides two actions for the lawyer to correct client document submissions:
 * - reclassifyDocument: reassigns a document to a different request_field by
 *   updating field_id directly on the documents table.
 * - renameDocument: sets a custom display filename on a document by updating
 *   display_filename directly on the documents table.
 *
 * Created Jun 2026.
 * Updated Jun 2026: removed override_field_id / override_display_filename / overridden_at
 *   columns (dropped in simplification migration); mutations now target field_id and
 *   display_filename directly.
 */

import { useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '../contexts/ToastContext';
import { captureError } from '../utils/errorReporting';

export function useDocumentOverride(onRefresh: () => void) {
  const { showToast } = useToast();

  const reclassifyDocument = useCallback(
    async (docId: string, targetFieldId: string) => {
      const { error } = await supabase
        .from('documents')
        .update({ field_id: targetFieldId })
        .eq('id', docId);

      if (error) {
        console.error('[useDocumentOverride] reclassifyDocument error:', error);
        captureError(error, { hook: 'useDocumentOverride', action: 'reclassifyDocument', docId, targetFieldId });
        showToast('Impossible de déplacer le document', 'error');
        return false;
      }

      showToast('Document déplacé', 'success');
      onRefresh();
      return true;
    },
    [showToast, onRefresh],
  );

  const renameDocument = useCallback(
    async (docId: string, newFilename: string) => {
      const trimmed = newFilename.trim();
      if (!trimmed) return false;

      const { error } = await supabase
        .from('documents')
        .update({ display_filename: trimmed })
        .eq('id', docId);

      if (error) {
        console.error('[useDocumentOverride] renameDocument error:', error);
        captureError(error, { hook: 'useDocumentOverride', action: 'renameDocument', docId });
        showToast('Impossible de renommer le document', 'error');
        return false;
      }

      showToast('Document renommé', 'success');
      onRefresh();
      return true;
    },
    [showToast, onRefresh],
  );

  return { reclassifyDocument, renameDocument };
}
